import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';

const LetThereBeAI = () => {
  useEffect(() => {
    // Set page title and meta tags
    document.title = 'Let There Be AI – Official Website of LetThereBe.ai';
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Let There Be AI is the official creative development studio behind LetThereBe.ai, an AI-powered platform that builds mobile apps, web apps, and digital experiences for businesses and founders.');
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      metaDescription.content = 'Let There Be AI is the official creative development studio behind LetThereBe.ai, an AI-powered platform that builds mobile apps, web apps, and digital experiences for businesses and founders.';
      document.head.appendChild(metaDescription);
    }
    
    // Update or create meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Let There Be AI, LetThereBe.ai, AI development studio, AI mobile app development, AI web design, AI-powered development platform');
    } else {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      metaKeywords.content = 'Let There Be AI, LetThereBe.ai, AI development studio, AI mobile app development, AI web design, AI-powered development platform';
      document.head.appendChild(metaKeywords);
    }
    
    // Add canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://www.lettherebe.ai/let-there-be-ai');
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = 'https://www.lettherebe.ai/let-there-be-ai';
      document.head.appendChild(canonicalLink);
    }
    
    // Update OG tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Let There Be AI – Official Website of LetThereBe.ai');
    }
    
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Let There Be AI is the official creative development studio behind LetThereBe.ai, an AI-powered platform that builds mobile apps, web apps, and digital experiences for businesses and founders.');
    }
    
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://www.lettherebe.ai/let-there-be-ai');
    }
    
    // Cleanup function to restore original meta tags when leaving the page
    return () => {
      document.title = 'LetThereBe.ai – AI-Powered App & Web Development Studio';
      if (metaDescription) {
        metaDescription.setAttribute('content', 'LetThereBe.ai is an AI-driven creative development platform that builds modern mobile apps, web apps, and digital experiences for businesses and founders.');
      }
      if (metaKeywords) {
        metaKeywords.setAttribute('content', 'Lettherebe.ai, AI development studio, mobile app development, web app development, AI web design, custom app studio, Emergent, React Native, SaaS development');
      }
      if (canonicalLink) {
        canonicalLink.remove();
      }
      if (ogTitle) {
        ogTitle.setAttribute('content', 'LetThereBe.ai – AI App & Web Development');
      }
      if (ogDescription) {
        ogDescription.setAttribute('content', 'A new kind of creation platform: We build mobile apps, web apps, and interactive experiences using AI and modern frameworks.');
      }
      if (ogUrl) {
        ogUrl.setAttribute('content', 'https://www.lettherebe.ai');
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-8 text-sm">
            <Link to="/" className="text-violet-600 hover:text-violet-700">Home</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-600">Let There Be AI</span>
          </nav>
          
          {/* H1 Header */}
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Let There Be AI
          </h1>
          
          {/* Intro Paragraph */}
          <p className="text-xl text-gray-700 leading-relaxed mb-12">
            Let There Be AI is the official brand and creative development platform behind LetThereBe.ai — a modern AI-powered studio that designs and builds custom mobile apps, web apps, and interactive digital products for founders, entrepreneurs, and businesses.
          </p>
          
          {/* Brand Definition Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Brand Definition
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              The phrase "Let There Be AI" is not just a cultural expression — it represents an innovation studio and technology partner that helps people bring ideas to life through artificial intelligence, automation, modern frameworks, and intuitive design. LetThereBe.ai exists to make creation faster, smarter, and more accessible for anyone building digital products.
            </p>
          </section>
          
          {/* Services Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Services
            </h2>
            <p className="text-lg text-gray-700 mb-4">
              Let There Be AI specializes in:
            </p>
            <ul className="space-y-3 text-lg text-gray-700">
              <li className="flex items-start">
                <span className="text-violet-600 mr-3">•</span>
                <span>AI-powered mobile app development</span>
              </li>
              <li className="flex items-start">
                <span className="text-violet-600 mr-3">•</span>
                <span>Web app and SaaS platform development</span>
              </li>
              <li className="flex items-start">
                <span className="text-violet-600 mr-3">•</span>
                <span>Creative prototyping and interactive design</span>
              </li>
              <li className="flex items-start">
                <span className="text-violet-600 mr-3">•</span>
                <span>Automation and AI integrations</span>
              </li>
              <li className="flex items-start">
                <span className="text-violet-600 mr-3">•</span>
                <span>App store deployment (iOS + Android)</span>
              </li>
              <li className="flex items-start">
                <span className="text-violet-600 mr-3">•</span>
                <span>Web infrastructure, hosting, and backend APIs</span>
              </li>
            </ul>
            <p className="text-lg text-gray-700 mt-6 leading-relaxed">
              Whether clients need a simple MVP or a large-scale platform, LetThereBe.ai uses AI and modern development tools to build quickly, creatively, and cost-effectively.
            </p>
          </section>
          
          {/* Brand Identity Statement */}
          <section className="mb-12 p-6 bg-gradient-to-r from-violet-50 to-pink-50 rounded-xl border border-violet-100">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Brand Identity Statement
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Let There Be AI™ is a branded development studio operated by LetThereBe.ai. All references to "Let There Be AI" as a company or platform refer to this official entity and website.
            </p>
          </section>
          
          {/* CTA Section */}
          <section className="text-center py-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Build Your Vision?
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              Connect with our team to discuss your next project
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link 
                to="/#contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white bg-gradient-to-r from-violet-600 to-pink-600 rounded-lg hover:shadow-lg transition-all duration-300"
              >
                Get Started
              </Link>
              <Link 
                to="/#portfolio"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-violet-600 bg-white border-2 border-violet-600 rounded-lg hover:bg-violet-50 transition-all duration-300"
              >
                View Our Work
              </Link>
            </div>
          </section>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default LetThereBeAI;
